import { useCallback, useEffect, useRef, useState } from 'react';
import logsWebSocketService from '../services/logsWebSocket';
import {
    ImportJobStatus,
    ImportJobView,
    cancelImportJob,
    getImportJob,
    isFinalImportStatus,
    retryImportJob,
} from '../services/importJobApi';
import { importJobActions, importJobLabel, isImportStuck } from '../utils/importJobView';

export const IMPORT_POLL_MS = 2000;
/** With the socket up, still read the job now and then in case an event was missed. */
const SAFETY_POLL_EVERY = 8;

interface Live {
    status: ImportJobStatus;
    error: string | null;
    lastChangeAt: number;
}

/**
 * Follows one import job: the `import-job` socket event, with GET /imports/:id every 2 s while the
 * socket is not in the user's room. `onReady` fires once, with the full job view.
 */
export function useImportJob(importId: string | null, onReady?: (job: ImportJobView) => void) {
    const [view, setView] = useState<ImportJobView | null>(null);
    const [live, setLive] = useState<Live | null>(null);
    const [now, setNow] = useState(() => Date.now());
    const [acting, setActing] = useState<'retry' | 'cancel' | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const firedRef = useRef<string | null>(null);
    const onReadyRef = useRef(onReady);
    onReadyRef.current = onReady;

    const apply = useCallback((status: ImportJobStatus, error: string | null, at: number) => {
        setLive((prev) =>
            prev && prev.status === status && prev.error === error
                ? prev
                : { status, error, lastChangeAt: prev && prev.status === status ? prev.lastChangeAt : at },
        );
    }, []);

    const fetchJob = useCallback(async () => {
        if (!importId) return;
        try {
            const job = await getImportJob(importId);
            setView(job);
            apply(job.status, job.error, Date.parse(job.updatedAt) || Date.now());
        } catch {
            // the next tick reads it again
        }
    }, [importId, apply]);

    useEffect(() => {
        setView(null);
        setLive(null);
        setActionError(null);
        if (!importId) return;
        void fetchJob();
        const off = logsWebSocketService.onImportJob((event) => {
            if (event?.id !== importId) return;
            apply(event.status as ImportJobStatus, event.error ?? null, Date.now());
        });
        let tick = 0;
        const timer = setInterval(() => {
            tick += 1;
            setNow(Date.now());
            if (!logsWebSocketService.isInUserRoom() || tick % SAFETY_POLL_EVERY === 0) void fetchJob();
        }, IMPORT_POLL_MS);
        return () => {
            off();
            clearInterval(timer);
        };
    }, [importId, fetchJob, apply]);

    const status = live?.status ?? null;
    const final = status != null && isFinalImportStatus(status);

    // the socket event has no resultDataConfigId: read the job once it says READY
    useEffect(() => {
        if (status === 'READY' && view?.status !== 'READY') void fetchJob();
    }, [status, view?.status, fetchJob]);

    useEffect(() => {
        if (view?.status === 'READY' && firedRef.current !== view.id) {
            firedRef.current = view.id;
            onReadyRef.current?.(view);
        }
    }, [view]);

    const act = useCallback(
        async (kind: 'retry' | 'cancel') => {
            if (!importId) return;
            setActing(kind);
            setActionError(null);
            try {
                const job = await (kind === 'retry' ? retryImportJob : cancelImportJob)(importId);
                setView(job);
                // a fresh attempt: the "stuck" clock starts again
                setLive({ status: job.status, error: job.error, lastChangeAt: Date.now() });
            } catch (e: any) {
                setActionError(e?.response?.data?.message ?? e?.message ?? `Could not ${kind} the import.`);
                void fetchJob();
            } finally {
                setActing(null);
            }
        },
        [importId, fetchJob],
    );

    const stuck = live ? isImportStuck(live.status, live.lastChangeAt, now) : false;
    const actions = live ? importJobActions(live.status, live.error, stuck) : { retry: false, cancel: false };

    return {
        view,
        status,
        error: live?.error ?? null,
        label: live ? importJobLabel(live.status, live.error) : 'Loading…',
        final,
        stuck,
        canRetry: actions.retry,
        canCancel: actions.cancel,
        acting,
        actionError,
        retry: () => act('retry'),
        cancel: () => act('cancel'),
    };
}
